/**
 * BoxTab — extracted from /app/frontend/src/pages/PalGame.jsx
 * as part of the per-tab split. Behaviour identical to the
 * pre-split monolith; only the file boundary changed.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, RarityBadge } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { PalXpPreview } from "@/components/PalXpPreview";
import { PayoutFlash } from "@/components/PayoutFlash";
import { AchievementUnlocked } from "@/components/AchievementUnlocked";
import { MasterTrainerCinematic } from "@/components/MasterTrainerCinematic";
import { useMasterTrainers } from "@/hooks/useMasterTrainers";
import {
  Swords, Sparkles, Zap, Compass, Trophy, Layers, Target, Castle,
  ArrowLeftRight, Crosshair, Heart, ChevronLeft, BookOpen, Search,
} from "lucide-react";
import { PalPicker, ActionWithPal, firePalgamePayout, firePalgameDefeat } from "./_shared";
import { SendFromBoxButton } from "@/components/deliver/SendToGame";

export function BoxTab() {
  const [pals, setPals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("level");
  const [filter, setFilter] = useState("all");
  // Power-user filters — name search, lucky-only toggle, min passive
  // tier (4 = Legend+, 3 = Tier 3+, etc.), and minimum level. Each
  // narrows the visible list further; resets via a single "Clear all".
  const [search, setSearch] = useState("");
  const [luckyOnly, setLuckyOnly] = useState(false);
  const [minTier, setMinTier] = useState(0);   // 0 = no min, 4 = legendary
  const [minLevel, setMinLevel] = useState(0); // 0 = any
  // Clicking a card opens the detail modal — we store the pal NAME and let
  // the modal fetch the rich payload (IVs / skills / matchups) on demand
  // so we don't bloat the box endpoint.
  const [detailPal, setDetailPal] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/palgame/box"),
      api.get("/pal/user-pals").catch(() => ({ data: { pals: [] } })),
    ]).then(([box, deliver]) => {
      // Merge `cost`, `pal_id`, and the tier-tagged passive list from
      // `/pal/user-pals` into the box rows. /pal/user-pals now returns
      // `passives: [{name, tier}, ...]` so the Box card can colour each
      // chip by tier without re-fetching the passive library.
      const extraByPalId = new Map();
      (deliver.data?.pals || []).forEach((d) => {
        if (d.pal_id != null) extraByPalId.set(d.pal_id, d);
      });
      const merged = (box.data.pals || []).map((p) => {
        const x = extraByPalId.get(p.pal_id);
        return {
          ...p,
          cost: x?.cost ?? 0,
          // `passives_with_tier` is the rich shape ([{name, tier}]);
          // we keep `p.passives` (raw JSON string from /palgame/box)
          // untouched for any consumer that still reads it as a string.
          passives_with_tier: Array.isArray(x?.passives) ? x.passives : [],
        };
      });
      setPals(merged);
      setTotal(box.data.total_available || 0);
    }).finally(() => setLoading(false));
  }, []);

  const sorted = useMemo(() => {
    let arr = [...pals];
    if (filter !== "all") arr = arr.filter((p) => p.rarity?.toLowerCase() === filter);
    if (luckyOnly) arr = arr.filter((p) => !!p.is_lucky);
    if (minLevel > 0) arr = arr.filter((p) => (p.level || 0) >= minLevel);
    if (minTier > 0) {
      arr = arr.filter((p) =>
        (p.passives_with_tier || []).some((pv) => (pv.tier ?? 0) >= minTier)
      );
    }
    const needle = search.trim().toLowerCase();
    if (needle) arr = arr.filter((p) => (p.pal_name || "").toLowerCase().includes(needle));
    arr.sort((a, b) => sort === "level" ? (b.level - a.level) : ((b.rarity_value || 0) - (a.rarity_value || 0)));
    return arr;
  }, [pals, sort, filter, luckyOnly, minTier, minLevel, search]);

  const clearFilters = () => {
    setFilter("all"); setLuckyOnly(false);
    setMinTier(0); setMinLevel(0); setSearch("");
  };
  const filtersActive = filter !== "all" || luckyOnly || minTier > 0 || minLevel > 0 || !!search.trim();

  if (loading) return <Loader />;

  return (
    <div className="fade-up">
      {/* Header row — count + sort */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="text-sm text-[var(--muted)]" data-testid="box-collection-count">
          Collection: <span className="glow-teal">{sorted.length}</span>
          <span className="text-[var(--muted)]">{
            sorted.length !== pals.length ? ` of ${pals.length} shown` : ""
          }</span> · {total} total
        </div>
        <div className="flex gap-2">
          <select className="input" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="box-sort">
            <option value="level">Sort: Level</option>
            <option value="rarity">Sort: Rarity</option>
          </select>
        </div>
      </div>

      {/* Filter bar — name search + rarity + lucky + min tier + min level + clear */}
      <div
        className="glass-card p-3 mb-4 flex flex-wrap items-center gap-2"
        data-testid="box-filter-bar"
      >
        <div className="relative flex-1 min-w-[12rem]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input w-full pl-8 text-sm"
            data-testid="box-filter-search"
          />
        </div>

        <select
          className="input text-sm"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="box-filter"
          title="Rarity"
        >
          <option value="all">All rarities</option>
          <option value="common">Common</option>
          <option value="uncommon">Uncommon</option>
          <option value="rare">Rare</option>
          <option value="epic">Epic</option>
          <option value="legendary">Legendary</option>
        </select>

        <select
          className="input text-sm"
          value={minTier}
          onChange={(e) => setMinTier(parseInt(e.target.value, 10))}
          data-testid="box-filter-min-tier"
          title="Show only pals with at least one passive at this rank or higher"
        >
          <option value={0}>Any passive</option>
          <option value={1}>Tier 1+ passive</option>
          <option value={2}>Tier 2+ passive</option>
          <option value={3}>Tier 3+ passive</option>
          <option value={4}>Legend / Tier 4 only</option>
        </select>

        <select
          className="input text-sm"
          value={minLevel}
          onChange={(e) => setMinLevel(parseInt(e.target.value, 10))}
          data-testid="box-filter-min-level"
          title="Minimum level"
        >
          <option value={0}>Any level</option>
          <option value={10}>Lv 10+</option>
          <option value={20}>Lv 20+</option>
          <option value={30}>Lv 30+</option>
          <option value={40}>Lv 40+</option>
          <option value={50}>Lv 50+</option>
          <option value={60}>Lv 60+</option>
          <option value={65}>Lv 65 only</option>
        </select>

        <button
          type="button"
          onClick={() => setLuckyOnly((v) => !v)}
          className={
            "px-3 py-1.5 rounded-md border text-xs uppercase tracking-wider flex items-center gap-1.5 transition-colors " +
            (luckyOnly
              ? "border-yellow-400/60 bg-yellow-500/10 text-yellow-200"
              : "border-white/10 hover:border-white/30 text-[var(--muted)]")
          }
          data-testid="box-filter-lucky"
          title="Show only Lucky pals"
          aria-pressed={luckyOnly}
        >
          <Sparkles size={13} /> Lucky
        </button>

        {filtersActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="px-3 py-1.5 rounded-md border border-white/10 hover:border-red-400/40 hover:bg-red-500/5 text-xs uppercase tracking-wider text-[var(--muted)] hover:text-red-200"
            data-testid="box-filter-clear"
            title="Reset every filter"
          >
            Clear
          </button>
        )}
      </div>

      {sorted.length === 0 ? (
        <p className="text-[var(--muted)] text-center py-12">
          {filtersActive
            ? "No pals match your filters. Try clearing some."
            : "No pals match. Catch some!"}
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {sorted.map((p, i) => {
            // After the 2026-06-08 multi-instance refactor a player can
            // own several of the same species. The list itself stays
            // flat (one card per instance) so duplicates appear next
            // to each other — keyed on `pal_id` to avoid React-key
            // collisions.
            const dupeCount = sorted.filter((x) => x.pal_name === p.pal_name).length;
            const dupeIndex = sorted
              .filter((x) => x.pal_name === p.pal_name)
              .findIndex((x) => x.pal_id === p.pal_id) + 1;
            return (
              <div
                key={p.pal_id ?? p.pal_name}
                role="button"
                tabIndex={0}
                onClick={() => setDetailPal(p.pal_name)}
                onKeyDown={(e) => {
                  // Treat Enter / Space like a click so the card stays
                  // keyboard-accessible after dropping the wrapping
                  // <button> (HTML5 forbids button-in-button, so we use
                  // a div + ARIA role to allow `<SendFromBoxButton>` to
                  // be a real <button> inside).
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetailPal(p.pal_name);
                  }
                }}
                className={`glass-card p-3 rarity-${p.rarity?.toLowerCase()} text-left transition-transform hover:scale-[1.02] hover:border-[var(--teal)]/50 cursor-pointer relative outline-none focus-visible:ring-2 focus-visible:ring-[var(--teal)] ${p.is_lucky ? "ring-2 ring-yellow-300/60 shadow-[0_0_18px_rgba(255,196,90,0.35)]" : ""}`}
                style={{ animationDelay: `${i * 20}ms` }}
                data-testid={`pal-card-${p.pal_id ?? p.pal_name}`}
                aria-label={`View ${p.pal_name} details`}
              >
                {p.is_lucky && (
                  <span
                    className="absolute top-1.5 right-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-900/70 border border-yellow-400/40 text-yellow-200 uppercase tracking-wider font-semibold pointer-events-none"
                    data-testid={`pal-card-lucky-${p.pal_id ?? p.pal_name}`}
                  >✨</span>
                )}
                {dupeCount > 1 && (
                  <span
                    className="absolute top-1.5 left-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-black/60 border border-[var(--teal-soft)] text-[var(--teal)] uppercase tracking-wider font-mono pointer-events-none"
                    title={`Instance ${dupeIndex} of ${dupeCount}`}
                  >
                    #{dupeIndex}/{dupeCount}
                  </span>
                )}
                <PalPortrait name={p.pal_name} size="w-full aspect-square" className="mb-2" />
                <div className="text-sm truncate">{p.pal_name}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-[var(--muted)]">Lv {p.level}</span>
                  <RarityBadge rarity={p.rarity} />
                </div>
                {p.elements?.length ? (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {p.elements.slice(0, 3).map((e) => (
                      <ElementChip key={e} element={e} size={12} showLabel={false} />
                    ))}
                  </div>
                ) : null}
                {p.passives_with_tier?.length > 0 && (
                  <div
                    className="flex flex-wrap gap-1 mt-1.5"
                    data-testid={`pal-card-passives-${p.pal_id ?? p.pal_name}`}
                  >
                    {p.passives_with_tier.slice(0, 4).map((pv) => (
                      <PassiveTierChip key={pv.name} passive={pv} />
                    ))}
                  </div>
                )}
                <div
                  // Stop the wrapping card's click handler from also
                  // firing when the user interacts with the inner Send
                  // button (or any future controls). Without this, a
                  // tap on Send opens the detail modal at the same
                  // time as the delivery confirm.
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <SendFromBoxButton
                    pal={p}
                    onSuccess={() => setPals((prev) => prev.filter((x) => x.pal_id !== p.pal_id))}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {detailPal && (
        <PalDetailModal palName={detailPal} onClose={() => setDetailPal(null)} />
      )}
    </div>
  );
}

/**
 * <PalDetailModal> — the click-into card for one of your owned pals.
 * Fetches `/palgame/box/:name` on mount and renders five sections:
 *
 *   1. Header        portrait + name (nickname if set) + rarity badge
 *   2. Level / XP    progress bar with `xp/next_level_xp`
 *   3. Stats         effective HP/Atk/Def + IV bars (0-31 scale)
 *   4. Skills        learned now + upcoming with level requirements
 *   5. Matchups      "Strong vs / Weak to" pill rows from the type chart
 */
function PalDetailModal({ palName, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/palgame/box/${encodeURIComponent(palName)}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(apiError(e)));
  }, [palName]);

  const xpPct = data
    ? Math.min(100, Math.round((data.experience / Math.max(1, data.next_level_xp)) * 100))
    : 0;

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="pal-detail-modal"
    >
      <div
        className={`glass-card max-w-2xl w-full max-h-[88vh] overflow-y-auto ${data ? `rarity-${data.rarity?.toLowerCase()}` : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {err && <div className="p-6 text-[var(--warning)]">{err}</div>}
        {!data && !err && <div className="p-12"><Loader /></div>}
        {data && (
          <>
            {/* Header */}
            <div className="flex items-start gap-4 p-6 pb-4 border-b border-white/5">
              <PalPortrait name={data.name} size="w-28 h-28 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <h2 className="text-2xl truncate" data-testid="pal-detail-name">{data.nickname || data.name}</h2>
                  {data.nickname && <span className="text-xs text-[var(--muted)]">({data.name})</span>}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <RarityBadge rarity={data.rarity} />
                  {data.elements?.map((e) => <ElementChip key={e} element={e} size={14} showLabel={true} />)}
                </div>
                {data.description && (
                  <p className="text-xs text-[var(--muted)] mt-2 line-clamp-3">{data.description}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-[var(--muted)] hover:text-[var(--bone)] text-xl leading-none"
                aria-label="Close"
                data-testid="pal-detail-close"
              >×</button>
            </div>

            {/* Level + XP bar */}
            <div className="px-6 py-4 border-b border-white/5">
              <div className="flex items-end justify-between mb-1.5">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">Level</div>
                  <div className="text-3xl glow-teal leading-none" data-testid="pal-detail-level">{data.level}</div>
                </div>
                <div className="text-xs text-[var(--muted)]">
                  {data.experience.toLocaleString()} / {data.next_level_xp.toLocaleString()} XP
                </div>
              </div>
              <div className="h-2 bg-black/40 rounded overflow-hidden">
                <div
                  className="h-full bg-[var(--teal)] transition-all"
                  style={{ width: `${xpPct}%`, boxShadow: "0 0 8px var(--teal-glow)" }}
                />
              </div>
              <PalXpPreview level={data.level} testid="pal-detail-xp-preview" />
            </div>

            {/* Stats + IVs */}
            <div className="px-6 py-4 border-b border-white/5">
              <h3 className="label mb-3">
                Stats &amp; IVs
                {data.is_lucky && (
                  <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-900/60 border border-yellow-400/40 text-[10px] uppercase tracking-[0.2em] text-yellow-200"
                        data-testid="pal-lucky-pill">
                    ✨ Lucky
                  </span>
                )}
              </h3>
              <div className="space-y-2.5">
                {["HP", "Attack", "Defense"].map((k) => (
                  <StatRow
                    key={k}
                    label={k}
                    effective={data.effective_stats[k]}
                    iv={data.ivs[k]}
                  />
                ))}
              </div>
              <div className="text-[10px] text-[var(--muted)] mt-3 italic">
                Talents (IVs) are 0–100. 100 = +30 % stat scaling. Roll on catch, locked forever — for now.
              </div>
            </div>

            {/* Passives */}
            <div className="px-6 py-4 border-b border-white/5">
              <h3 className="label mb-3 flex items-center gap-2">
                Passives <span className="text-[10px] text-[var(--muted)] font-mono">{data.passive_slots_used} / 4</span>
              </h3>
              {(!data.passives || data.passives.length === 0) ? (
                <p className="text-xs text-[var(--muted)] italic">
                  No passives rolled — this one slipped through the net.
                </p>
              ) : (
                <div className="space-y-2" data-testid="pal-passives">
                  {data.passives.map((p) => (
                    <PassiveRow key={p.name} passive={p} />
                  ))}
                </div>
              )}
            </div>

            {/* Skills */}
            <div className="px-6 py-4 border-b border-white/5">
              <h3 className="label mb-3">Attacks ({data.skills_learned.length} learned)</h3>
              {data.skills_learned.length === 0 ? (
                <p className="text-xs text-[var(--muted)]">No skills learned yet — keep battling to gain levels.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {data.skills_learned.map((s) => (
                    <SkillRow key={s.name} skill={s} learned />
                  ))}
                </div>
              )}
              {data.skills_upcoming.length > 0 && (
                <>
                  <h4 className="label mt-4 mb-2 text-[var(--muted)]">Upcoming</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {data.skills_upcoming.slice(0, 6).map((s) => (
                      <SkillRow key={s.name} skill={s} learned={false} />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Matchups */}
            <div className="px-6 py-4">
              <h3 className="label mb-3">Type Matchups</h3>
              <MatchupRow label="Strong against" elements={data.matchups.strong_vs} tone="strong" empty="No type advantage" />
              <MatchupRow label="Weak when attacking" elements={data.matchups.weak_when_attacking} tone="weak" empty="No type disadvantage" />
              <MatchupRow label="Takes super-effective from" elements={data.matchups.weak_to} tone="weak" empty="No known weaknesses" />
              <MatchupRow label="Resists" elements={data.matchups.resists} tone="strong" empty="No known resistances" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatRow({ label, effective, iv }) {
  // Talents are 0-100 now (in-game scale). 80+ = teal, 50+ = bone, else muted.
  const pct = Math.min(100, Math.max(0, iv || 0));
  const color = pct >= 80 ? "var(--teal)" : pct >= 50 ? "var(--bone)" : "var(--muted)";
  return (
    <div className="flex items-center gap-3 text-sm" data-testid={`pal-stat-${label.toLowerCase()}`}>
      <div className="w-16 text-[var(--muted)] text-xs">{label}</div>
      <div className="w-16 text-right tabular-nums">{effective}</div>
      <div className="flex-1 h-2 bg-black/40 rounded overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="w-14 text-right text-xs tabular-nums">
        <span style={{ color }}>{pct}/100</span>
      </div>
    </div>
  );
}

/**
 * Tier-coloured passive chip used on the Box-card grid. The colour
 * mirrors the in-game passive rank (+4 gold, +3 fuchsia, +2 teal,
 * +1 muted, ≤0 negative red) so a player can spot legendary rolls at
 * a glance without opening the detail modal.
 */
function PassiveTierChip({ passive }) {
  const tier = passive?.tier ?? 0;
  // Single source of truth for the chip palette. Keep these aligned
  // with the Pal Builder's passive picker (`PassiveRankBadge`) so the
  // same passive reads consistently across the site.
  const palette = {
    4: "bg-yellow-500/10 border-yellow-300/40 text-yellow-200",
    3: "bg-fuchsia-500/10 border-fuchsia-300/40 text-fuchsia-200",
    2: "bg-teal-500/10 border-teal-300/40 text-teal-200",
    1: "bg-white/10 border-white/20 text-[var(--bone)]",
  };
  const cls = tier < 0
    ? "bg-red-500/10 border-red-400/40 text-red-200"
    : (palette[tier] || "bg-white/5 border-white/10 text-[var(--muted)]");
  return (
    <span
      className={`text-[9px] px-1.5 py-0.5 rounded border uppercase tracking-wider font-mono leading-tight ${cls}`}
      title={`${passive.name} · tier ${tier}`}
      data-testid={`pal-card-passive-${(passive.name || "").replace(/\s/g, "-")}`}
    >
      {passive.name}
    </span>
  );
}


function PassiveRow({ passive }) {
  const rank = passive.rank;
  const arrowSrc = rank
    ? `${process.env.REACT_APP_BACKEND_URL}/api/assets/passives/T_icon_skillstatus_rank_arrow_0${Math.min(4, Math.abs(rank))}${rank < 0 ? "_negative" : ""}.webp`
    : null;
  return (
    <div className="rounded border border-white/10 bg-black/30 px-3 py-2"
         data-testid={`pal-passive-${(passive.name || "").replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2">
        {arrowSrc && (
          <img src={arrowSrc} alt="" className="w-4 h-4 object-contain" loading="lazy" />
        )}
        <div className="text-sm font-medium flex-1 truncate">{passive.name}</div>
      </div>
      {Array.isArray(passive.effects) && passive.effects.length > 0 && (
        <div className="text-[10px] text-[var(--muted)] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {passive.effects.map((e, i) => (
            <span key={i}>{e.stat} <span className="text-[var(--bone)]">{e.value}</span></span>
          ))}
        </div>
      )}
    </div>
  );
}

function SkillRow({ skill, learned }) {
  return (
    <div
      className={`rounded border px-3 py-2 ${learned ? "border-white/10 bg-black/30" : "border-white/5 bg-black/15 opacity-60"}`}
      data-testid={`pal-skill-${skill.name.replace(/\s/g, "-")}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm truncate">{skill.name}</div>
        {!learned && <div className="text-[10px] text-[var(--muted)] shrink-0">Lv {skill.level_req}</div>}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <ElementChip element={skill.element} size={11} showLabel={true} />
        {skill.power != null && (
          <span className="text-[10px] text-[var(--muted)]">PWR {skill.power}</span>
        )}
        {skill.cooldown != null && (
          <span className="text-[10px] text-[var(--muted)]">CT {skill.cooldown}s</span>
        )}
      </div>
    </div>
  );
}

function MatchupRow({ label, elements, tone, empty }) {
  const tint = tone === "strong"
    ? "border-[var(--teal)]/40 bg-[var(--teal-soft)]"
    : "border-[var(--warning)] bg-[rgba(255,107,53,0.08)]";
  return (
    <div className="mb-2.5 last:mb-0 flex items-start gap-3">
      <div className="text-xs text-[var(--muted)] w-44 shrink-0 pt-1">{label}</div>
      {elements?.length ? (
        <div className="flex flex-wrap gap-1.5">
          {elements.map((e) => (
            <span key={e} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${tint}`}>
              <ElementChip element={e} size={11} showLabel={false} /> {e}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-xs text-[var(--muted)] italic pt-1">{empty}</div>
      )}
    </div>
  );
}

